import type { Sprite } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import type { TweenEasing } from "../../runner/Easing";
import type { GridCells } from "../../view/grid/GridLayer";
import type { WinHighlightTuning } from "./WinHighlightTuning";

/**
 * The dim's view of the per-sprite exposure owner: a negative exposure darkens through the same
 * owner a shine lifts with, one value per sprite, baseline 0.
 */
export interface SymbolDimExposurePort {
  rampExposure(sprite: Sprite | null, target: number, seconds: number, easing: TweenEasing, onComplete?: () => void, startDelaySeconds?: number): void;
  release(sprite: Sprite): void;
}

/**
 * The dim over the non-winning symbols across one highlight session: the first win dims the rest,
 * later wins reveal their cells (or, in `dimWinners` mode, every cell dims until its own shine
 * lifts it), the session ends with a fade back or an instant release.
 */
export class SymbolDimSession {
  /** Grid index → the dimmed sprite; the exposure owner drives it, this tracks which. */
  private readonly dimmed = new Map<number, Sprite>();
  private active = false;
  /** Bumped per end, so a late fade cannot end a newer session. */
  private generation = 0;
  /** `dimWinners` mode: cells revealed this round stay lit across re-dims. */
  private readonly revealed = new Set<number>();

  public constructor(
    private readonly cells: GridCells,
    private readonly owner: SymbolDimExposurePort | undefined,
    private readonly tuning: WinHighlightTuning,
    private readonly runner: PresentationTaskRunner,
  ) {}

  /** A win line highlighted: the first opens the session (dims), the next reveal their winners. */
  public onWin(positionMask: string, winners: Set<number>): void {
    if (!this.active) {
      this.dim(positionMask, winners);
      this.active = true;
    } else if (!this.tuning.dimWinners) {
      for (const index of winners) this.reveal(index);
    }
  }

  /** A winner lit by its own shine: bookkeeping only. */
  public revealByShine(index: number): void {
    this.reveal(index, true);
  }

  /** Specific cells lit mid-session (a scatter collect after a win); the rest stay dimmed. */
  public revealCells(indices: number[]): void {
    for (const index of indices) {
      const sprite = this.dimmed.get(index);
      if (!sprite) continue;
      this.dimmed.delete(index);
      this.owner?.rampExposure(sprite, 0, this.tuning.dimFadeSeconds, this.tuning.dimFadeEasing, () => this.owner?.release(sprite));
    }
  }

  /** The session's end: `immediate` releases now, else the dimmed cells fade back; `keepMaterial` drops the tracking but leaves the exposure for the pool to reset. */
  public async end(options?: { immediate?: boolean; keepMaterial?: boolean }): Promise<void> {
    if (!this.active && this.dimmed.size === 0) return;
    const generation = ++this.generation;
    await this.release(options?.immediate ?? false, options?.keepMaterial ?? false);
    if (generation === this.generation) this.active = false;
  }

  public clearRevealed(): void {
    this.revealed.clear();
  }

  private dim(positionMask: string, winners: Set<number>): void {
    if (!this.owner) return;
    const t = this.tuning;
    for (let i = 0; i < positionMask.length; i++) {
      if (!t.dimWinners ? winners.has(i) : this.revealed.has(i)) continue;
      const sprite = this.cells.getSymbolViewAtIndex(i)?.symbolSprite;
      if (!sprite) continue;
      this.dimmed.set(i, sprite);
      this.owner.rampExposure(sprite, t.dimExposure, t.dimFadeSeconds, t.dimFadeEasing, undefined, t.dimStartDelayMilliseconds / 1000);
    }
  }

  private reveal(index: number, byShine = false): void {
    this.revealed.add(index);
    const sprite = this.dimmed.get(index);
    if (!sprite) return;
    this.dimmed.delete(index);
    if (byShine) return;
    this.owner?.rampExposure(sprite, 0, this.tuning.dimFadeSeconds, this.tuning.dimFadeEasing, () => this.owner?.release(sprite));
  }

  private async release(immediate: boolean, keepMaterial: boolean): Promise<void> {
    const t = this.tuning;
    const startDelaySeconds = t.dimEndStartDelayMilliseconds / 1000;
    const sprites = [...this.dimmed.values()];
    this.dimmed.clear();
    if (keepMaterial || !this.owner || sprites.length === 0) return;
    if (immediate || (t.dimEndFadeSeconds <= 0 && startDelaySeconds <= 0)) {
      for (const sprite of sprites) this.owner.release(sprite);
      return;
    }
    for (const sprite of sprites) this.owner.rampExposure(sprite, 0, t.dimEndFadeSeconds, t.dimFadeEasing, () => this.owner?.release(sprite), startDelaySeconds);
    await this.runner.sleep(startDelaySeconds + t.dimEndFadeSeconds);
  }
}
